import { useMemo } from 'react';
import { flatten, segmentWatts, type Segment } from '@/workouts/flatten';
import type { Step } from '@/workouts/model';
import { zoneOf } from '@/ride/metrics';
import { zoneColor } from '@/ui/zones';

interface Props {
  steps?: Step[];
  segments?: Segment[];
  ftp: number;
  height?: number;
  /** Position marker (s). */
  elapsedSec?: number;
  testId?: string;
}

/** Whole-workout chart: blocks coloured by Coggan zone, ramps as slopes, free rides grey (SPEC §7.2/§7.3). */
export function WorkoutGraph({ steps, segments, ftp, height = 120, elapsedSec, testId }: Props) {
  const segs = useMemo(() => segments ?? flatten(steps ?? []), [steps, segments]);
  const total = segs.reduce((m, s) => Math.max(m, s.start + s.durationSec), 0) || 1;
  const maxW =
    Math.max(
      ftp * 1.3,
      ...segs.map((s) =>
        Math.max(segmentWatts(s, s.start, ftp) ?? 0, segmentWatts(s, s.start + s.durationSec, ftp) ?? 0),
      ),
    ) * 1.05;
  const W = 1000;
  const H = 100;
  const x = (t: number) => (t / total) * W;
  const y = (watts: number) => H - (watts / maxW) * H;
  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="none"
      style={{ width: '100%', height, display: 'block', background: 'var(--bg)', borderRadius: 8 }}
      data-testid={testId}
    >
      <line
        x1={0}
        x2={W}
        y1={y(ftp)}
        y2={y(ftp)}
        stroke="rgba(255,255,255,0.25)"
        strokeDasharray="6 6"
        vectorEffect="non-scaling-stroke"
      />
      {segs.map((s) => {
        const a = segmentWatts(s, s.start, ftp);
        const b = segmentWatts(s, s.start + s.durationSec, ftp);
        const x0 = x(s.start);
        const x1 = x(s.start + s.durationSec);
        if (a === null || b === null) {
          const h = y(ftp * 0.5);
          return (
            <rect
              key={s.index}
              x={x0}
              y={h}
              width={Math.max(0.5, x1 - x0)}
              height={H - h}
              fill="#3a3734"
              opacity={0.8}
            />
          );
        }
        const avg = (a + b) / 2;
        return (
          <polygon
            key={s.index}
            points={`${x0},${H} ${x0},${y(a)} ${x1},${y(b)} ${x1},${H}`}
            fill={zoneColor(zoneOf(avg, ftp))}
            stroke="rgba(0,0,0,0.35)"
            strokeWidth={0.5}
            vectorEffect="non-scaling-stroke"
          />
        );
      })}
      {elapsedSec !== undefined && (
        <line
          x1={x(elapsedSec)}
          x2={x(elapsedSec)}
          y1={0}
          y2={H}
          stroke="#fff"
          strokeWidth={2}
          vectorEffect="non-scaling-stroke"
          data-testid="workout-marker"
        />
      )}
    </svg>
  );
}
